import { interpolateStops } from 'helper/Meteorology/TrackingForecast/DetailedView/heatmapColorScale';

/**
 * Escala divergente "Pastel" da tabela de Preços do DESSEM: verde abaixo da média, creme na
 * média, vermelho acima. São 9 amostras de uma rampa OKLCH (neutro 0.985/0.008/95, verde
 * 0.76/0.08/155, vermelho 0.74/0.095/25); a interpolação RGB entre stops vizinhos erra no máximo
 * 4/255 por canal, e t = 0.5 cai exatamente no stop do meio (o neutro).
 */
export const PALETA_PASTEL_DIVERGENTE = [
  '#88C19B', '#A0CDAF', '#BFDABD', '#E1E6D0', '#FCFAF4', '#F2DDC9', '#EFC2B3', '#E9A9A3', '#E1938C',
];

/**
 * Preço zerado (0 ou 0,01, típico das horas de excedente solar): vai para o cinza e fica fora do
 * mín/média/máx da coluna — senão a média despenca e todo o resto da coluna vira vermelho.
 */
export const ehPrecoZerado = (valor: number): boolean => Math.abs(valor) <= 0.01;

export interface EscalaDaColuna { min: number; media: number; max: number; }

/** Mín, média e máx da coluna ignorando os zerados; `null` se não sobrar nenhum valor. */
export function escalaDaColuna(valores: number[]): EscalaDaColuna | null {
  const validos = valores.filter((valor) => Number.isFinite(valor) && !ehPrecoZerado(valor));
  if (validos.length === 0) return null;
  return {
    min: Math.min(...validos),
    media: validos.reduce((soma, valor) => soma + valor, 0) / validos.length,
    max: Math.max(...validos),
  };
}

/**
 * Divergente com o neutro na média: abaixo dela, [mín, média] -> [0, 0.5]; acima, [média, máx]
 * -> [0.5, 1]. Cada metade é esticada separadamente, então o mínimo é sempre o verde mais forte e
 * o máximo o vermelho mais forte, mesmo com a média longe do meio da faixa. Coluna constante fica
 * toda no neutro (`max === min` antes de tudo: a média em ponto flutuante pode sair 1e-14 acima).
 */
export function fracaoDivergente(valor: number, { min, media, max }: EscalaDaColuna): number {
  if (max === min) return 0.5;
  if (valor < media) return 0.5 * ((valor - min) / (media - min));
  return max > media ? 0.5 + 0.5 * ((valor - media) / (max - media)) : 0.5;
}

export function corDoPreco(valor: number, escala: EscalaDaColuna): string {
  const [r, g, b] = interpolateStops(PALETA_PASTEL_DIVERGENTE, fracaoDivergente(valor, escala));
  return `rgb(${r}, ${g}, ${b})`;
}
